import React from "react";
import { Card, Row, Col, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faInfoCircle,
    faScaleBalanced,
    faPlay,
    faClipboardList,
    faExclamationTriangle,
    faBriefcase,
} from "@fortawesome/free-solid-svg-icons";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import Button from "components/base/Button";
import { useBalancoAtivo } from "hooks/useBalanco";
import { useEmpresas } from "providers/EmpresaProvider";
import BotaoCriarBalanco from "./components/BotaoCriarBalanco";
import BotaoPesquisarProdutos from "./components/BotaoPesquisarProdutos";
import BotaoCodigoBarras from "./components/BotaoCodigoBarras";
import BotaoFinalizarBalanco from "./components/BotaoFinalizarBalanco";
import BotaoEditarProduto from "./components/BotaoEditarProduto";
import BotaoExcluirProduto from "./components/BotaoExcluirProduto";
import LinkProduto from "components/common/LinkProduto";

// Breadcrumb items
const balancoBreadcrumbItems = [
    { label: "Operacional", path: "/operacional" },
    { label: "Balanço", path: "/operacional/balanco" }
];

const BalancoOperacional: React.FC = () => {
    const { selectedIds, empresas } = useEmpresas();
    const balancoQuery = useBalancoAtivo();

    const balanco = balancoQuery.data;
    const isLoading = balancoQuery.isLoading;
    const hasPermissionError = balancoQuery.isError;

    // Verificar se há múltiplas empresas selecionadas
    const hasMultipleEmpresas = selectedIds.length > 1;
    const empresaSelecionada = empresas.find(e => e.id === selectedIds[0]);

    // A página principal agora não gerencia mais estados de inclusão
    // Cada componente (BotaoCodigoBarras, BotaoPesquisarProdutos) gerencia seus próprios estados

    // A página principal agora é mais simples, sem lógica de inclusão de produtos



    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (hasPermissionError) {
        return (
            <div className="text-center py-5 text-danger-emphasis">
                {(balancoQuery.error as Error).message}
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={balancoBreadcrumbItems} />

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-2">
                        Balanço
                    </h2>
                    <p className="text-body-tertiary mb-0">
                        Gerencie o balanço de produtos da farmácia
                    </p>
                </div>

                {/* Mostrar empresa selecionada */}
                {empresaSelecionada && (
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center text-body-secondary">
                            <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                            <small>
                                <strong>{empresaSelecionada.nomeFantasia}</strong>
                            </small>
                        </div>
                        {balanco && (
                            <div className="d-flex align-items-center gap-1 ms-2">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faPlay} className="text-success-emphasis me-1" size="sm" />
                                    <small className="text-success-emphasis">Em Andamento</small>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Alerta para múltiplas empresas */}
            {hasMultipleEmpresas && (
                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                    <div className="text-warning-emphasis">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Múltiplas empresas selecionadas:</strong> Para trabalhar com balanços,
                        selecione apenas uma empresa no seletor de empresas. Atualmente está sendo usada: <strong>{empresaSelecionada?.nomeFantasia}</strong>
                    </div>
                </Alert>
            )}

            {/* Alerta quando nenhuma empresa está selecionada */}
            {selectedIds.length === 0 && (
                <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                    <div className="text-warning-emphasis">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <strong>Nenhuma empresa selecionada:</strong> Selecione uma empresa no seletor de empresas para trabalhar com balanços.
                    </div>
                </Alert>
            )}

            {!balanco && selectedIds.length > 0 ? (
                // Estado: Nenhum balanço ativo
                <div>
                    <Card className="text-center py-5 mb-4">
                        <Card.Body>
                            <FontAwesomeIcon icon={faScaleBalanced} size="4x" className="text-primary-emphasis mb-4" />
                            <h3 className="mb-3">Iniciar Novo Balanço</h3>
                            <p className="text-body-tertiary mb-4 lead">
                                Não há balanço em andamento. Você pode iniciar um novo balanço para controlar o estoque da farmácia.
                            </p>
                        </Card.Body>
                    </Card>

                    <Row className="justify-content-center">
                        <Col md={8} lg={6} className="mb-4">
                            <Card className="h-100 border-primary border-opacity-25 shadow-sm">
                                <Card.Body className="text-center p-4">
                                    <FontAwesomeIcon icon={faClipboardList} size="3x" className="text-primary-emphasis mb-3" />
                                    <h5 className="mb-3">Novo Balanço</h5>
                                    <p className="text-body-tertiary mb-4">
                                        Inicia um novo balanço para contagem física dos produtos da farmácia.
                                        Você pode escolher departamentos específicos ou trabalhar com todos os produtos.
                                        <br />
                                        <small className="text-body-secondary">
                                            <strong>Métodos de inclusão:</strong> Bipagem por código de barras (mais rápido) ou pesquisa manual por nome/fabricante
                                        </small>
                                    </p>
                                    <BotaoCriarBalanco
                                        variant="primary"
                                        disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                    />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mt-4">
                        <div className="text-info-emphasis">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>Como funciona o processo:</strong><br />
                            <div className="mt-2">
                                <strong>1. Inclusão de produtos:</strong> Use a bipagem por código de barras para incluir produtos rapidamente ou pesquise manualmente.<br />
                                <strong>2. Produtos controlados:</strong> Para medicamentos de controle especial, será obrigatório informar lote e validade.<br />
                                <strong>3. Finalização:</strong> Escolha entre ajustar apenas os produtos contados ou zerar o estoque dos produtos não incluídos por departamento.<br />
                                <strong>4. Auditoria:</strong> Todas as alterações de estoque são registradas no histórico para rastreabilidade.
                            </div>
                        </div>
                    </Alert>
                </div>
            ) : balanco ? (
                // Estado: Balanço ativo
                <div>
                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h5 className="mb-2">
                                        <FontAwesomeIcon icon={faScaleBalanced} className="me-2 text-primary-emphasis" />
                                        Balanço em Andamento
                                    </h5>
                                    <p className="text-body-tertiary mb-0">
                                        Data de criação: <strong>{balanco.data ? new Date(balanco.data).toLocaleDateString('pt-BR') : 'N/A'}</strong>
                                    </p>
                                </Col>
                                <Col md={4} className="text-end">
                                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                                        <BotaoFinalizarBalanco
                                            disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Botões de Ação */}
                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={8}>
                                    <h6 className="mb-3">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary-emphasis" />
                                        Incluir Produtos no Balanço
                                    </h6>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <BotaoCodigoBarras
                                            disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                        />
                                        <BotaoPesquisarProdutos
                                            disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                        />
                                    </div>
                                    <div className="text-body-secondary mt-2 small">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        <strong>Dica:</strong> Use "Código de Barras" para bipagem rápida e contínua de múltiplos produtos,
                                        ou "Pesquisar" para busca manual por nome, fabricante ou código.
                                        Para produtos controlados, o sistema solicitará lote e validade automaticamente.
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Lista de produtos do balanço */}
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Produtos do Balanço</h5>
                        </Card.Header>
                        <Card.Body>
                            {(!balanco.produtos || balanco.produtos.length === 0) ? (
                                <div className="text-center py-4">
                                    <FontAwesomeIcon icon={faClipboardList} size="3x" className="text-body-secondary mb-3" />
                                    <h5 className="text-body-secondary mb-3">Nenhum produto incluído no balanço</h5>
                                    <p className="text-body-secondary mb-4">
                                        Comece a contagem usando os botões acima:<br />
                                        <strong>• Código de Barras:</strong> Para bipagem rápida e contínua<br />
                                        <strong>• Pesquisar:</strong> Para busca manual por nome ou fabricante<br />
                                        <small className="text-body-secondary">
                                            Produtos controlados exigirão informação de lote e validade durante a inclusão.
                                        </small>
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Código</th>
                                                <th>Descrição</th>
                                                <th>Fabricante</th>
                                                <th>Quantidade</th>
                                                <th>Lote</th>
                                                <th>Estoque Atual</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {balanco.produtos.map((produto: any, index: number) => (
                                                <tr key={index}>
                                                    <td>{produto.produtoCodigo}</td>
                                                    <td>
                                                        <LinkProduto produtoId={produto.produtoId}>
                                                            {produto.nomeProduto}
                                                        </LinkProduto>
                                                    </td>
                                                    <td>{produto.fabricante || '-'}</td>
                                                    <td><span className="fw-bold">{produto.quantidade}</span></td>
                                                    <td>
                                                        {produto.lote ? (
                                                            <span className="badge bg-secondary">{produto.lote}</span>
                                                        ) : (
                                                            <span className="text-body-secondary">-</span>
                                                        )}
                                                    </td>
                                                    <td>{produto.estoqueAtual}</td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <BotaoEditarProduto
                                                                produto={{
                                                                    produtoId: produto.produtoId,
                                                                    nomeProduto: produto.nomeProduto,
                                                                    lote: produto.lote,
                                                                    quantidade: produto.quantidade
                                                                }}
                                                                disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                                            />
                                                            <BotaoExcluirProduto
                                                                produto={{
                                                                    produtoId: produto.produtoId,
                                                                    nomeProduto: produto.nomeProduto,
                                                                    lote: produto.lote,
                                                                    quantidade: produto.quantidade
                                                                }}
                                                                disabled={hasMultipleEmpresas || selectedIds.length === 0}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            ) : null}
        </div>
    );
};

export default BalancoOperacional; 